import { ArrowDownTrayIcon, EnvelopeIcon, PrinterIcon, TrashIcon } from '@heroicons/react/24/outline'
import { DataGrid, GridColumn, GridSort } from 'components/DataGrid'
import { HoverTip } from 'components/HoverTip'
import { GenerateModal } from 'pages/Servicing/Statements'
import { useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import {
  deleteServicingStatement,
  downloadServicingStatement,
  emailServicingStatement,
  getLoanStatements,
} from 'services'
import Api from 'services/api'
import { Button } from 'stories/components'

interface LoanStatementRow {
  id: number
  batchId?: number | null
  statementDate?: string
  periodStart?: string
  periodEnd?: string
  dueDate?: string
  amountDue?: number
  emailedAt?: string
  emailedTo?: string
  createdBy?: string
  createdAt?: string
}

const money = (v?: number) =>
  v == null ? '' : `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

/**
 * Every statement this loan has been sent, from batch runs and from printing it here, with the
 * same print options as the portfolio Generate Statements screen.
 */
export default function LoanStatements() {
  const loanNumber = Number(Api.getLoanNumber())
  const [loading, setLoading] = useState(true)
  const [rows, setRows] = useState<LoanStatementRow[]>([])
  const [sort, setSort] = useState<GridSort | null>(null)
  const [showPrint, setShowPrint] = useState(false)
  const [busyId, setBusyId] = useState<number | null>(null)

  const load = () => {
    setLoading(true)
    getLoanStatements(loanNumber)
      .then((res: any) => res?.success && setRows(res.data || []))
      .finally(() => setLoading(false))
  }
  useEffect(() => {
    load()
  }, [loanNumber])

  const onDownload = async (s: LoanStatementRow) => {
    setBusyId(s.id)
    try {
      const blob: Blob = await downloadServicingStatement({ id: s.id })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `statement-${loanNumber}-${s.statementDate || s.id}.pdf`
      a.click()
      URL.revokeObjectURL(url)
    } catch {
      toast('Could not download the statement.', { type: 'error' })
    } finally {
      setBusyId(null)
    }
  }

  const onEmail = async (s: LoanStatementRow) => {
    const again = s.emailedAt ? ` It was already sent to ${s.emailedTo || 'the borrower'}.` : ''
    if (!window.confirm(`Email the ${s.statementDate || ''} statement to the borrower?${again}`)) return
    setBusyId(s.id)
    try {
      const res: any = await emailServicingStatement({ id: s.id })
      toast(res?.message || (res?.success ? 'Statement emailed.' : 'Statement was not emailed.'), {
        type: res?.success ? 'success' : 'error',
      })
      if (res?.success) load()
    } finally {
      setBusyId(null)
    }
  }

  /**
   * Withdraw a statement that should not have gone out. It leaves the loan's statements, the loan
   * documents and the borrower portal together; an email already sent cannot be taken back, so the
   * confirm says so rather than letting the delete imply otherwise.
   */
  const onDelete = async (s: LoanStatementRow) => {
    const sent = s.emailedAt ? `\n\nIt was emailed to ${s.emailedTo || 'the borrower'} on ${s.emailedAt}. Deleting it here cannot unsend that email.` : ''
    if (
      !window.confirm(
        `Delete the ${s.statementDate || ''} statement?\n\nIt will be removed from this loan's statements, from the loan documents and from the borrower portal.${sent}`,
      )
    )
      return
    setBusyId(s.id)
    try {
      const res: any = await deleteServicingStatement(s.id)
      toast(res?.message || (res?.success ? 'Statement deleted.' : 'Statement was not deleted.'), {
        type: res?.success ? 'success' : 'error',
      })
      if (res?.success) load()
    } finally {
      setBusyId(null)
    }
  }

  const columns = useMemo<GridColumn<LoanStatementRow>[]>(
    () => [
      { key: 'statementDate', label: 'Statement Date', sortable: true, hideable: false, defaultWidth: 130 },
      {
        key: 'period',
        label: 'Period',
        defaultWidth: 190,
        render: (s) => `${s.periodStart ?? ''} to ${s.periodEnd ?? ''}`,
      },
      { key: 'dueDate', label: 'Due Date', sortable: true, defaultWidth: 110 },
      {
        key: 'amountDue',
        label: 'Amount Due',
        sortable: true,
        align: 'right',
        defaultWidth: 120,
        render: (s) => money(s.amountDue),
      },
      {
        key: 'batchId',
        label: 'Source',
        sortable: true,
        defaultWidth: 110,
        render: (s) => (s.batchId ? `Batch #${s.batchId}` : 'Printed'),
      },
      { key: 'createdBy', label: 'By', sortable: true, defaultWidth: 170 },
      {
        key: 'emailedAt',
        label: 'Emailed',
        sortable: true,
        defaultWidth: 170,
        render: (s) =>
          s.emailedAt ? (
            <span className="text-green-700 text-xs">{s.emailedTo || 'Sent'}</span>
          ) : (
            <span className="text-gray-400 text-xs">Not sent</span>
          ),
      },
      {
        key: 'actions',
        label: 'Actions',
        hideable: false,
        reorderable: false,
        defaultWidth: 120,
        render: (s) => (
          <div className={`flex items-center gap-3.5 text-slate-500 ${busyId === s.id ? 'opacity-40' : ''}`}>
            <HoverTip message="Download this statement">
              <button
                aria-label="Download statement"
                className="hover:text-shade-blue"
                disabled={busyId === s.id}
                onClick={() => onDownload(s)}
              >
                <ArrowDownTrayIcon className="h-4 w-4" />
              </button>
            </HoverTip>
            <HoverTip message="Email this statement to the borrower">
              <button
                aria-label="Email statement"
                className="hover:text-shade-blue"
                disabled={busyId === s.id}
                onClick={() => onEmail(s)}
              >
                <EnvelopeIcon className="h-4 w-4" />
              </button>
            </HoverTip>
            <HoverTip message="Delete this statement, and take it off the loan documents and the borrower portal">
              <button
                aria-label="Delete statement"
                className="hover:text-red-600"
                disabled={busyId === s.id}
                onClick={() => onDelete(s)}
              >
                <TrashIcon className="h-4 w-4" />
              </button>
            </HoverTip>
          </div>
        ),
      },
    ],
    [busyId, loanNumber],
  )

  const sortedRows = useMemo(() => {
    if (!sort) return rows
    const { key, dir } = sort
    return [...rows].sort((a, b) => {
      const av = (a as any)[key]
      const bv = (b as any)[key]
      const cmp =
        typeof av === 'number' && typeof bv === 'number'
          ? av - bv
          : String(av ?? '').localeCompare(String(bv ?? ''), undefined, { numeric: true })
      return dir === 'asc' ? cmp : -cmp
    })
  }, [rows, sort])

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
        <div>
          <h2 className="text-2xl font-bold">Statements</h2>
          <p className="text-sm text-gray-500">
            {rows.length} statement{rows.length === 1 ? '' : 's'} on this loan
          </p>
        </div>
        <Button color="blue" className="mb-0" onClick={() => setShowPrint(true)}>
          <span className="flex items-center gap-1.5">
            <PrinterIcon className="h-4 w-4" />
            Print Statement
          </span>
        </Button>
      </div>

      <DataGrid<LoanStatementRow>
        gridKey="loan-servicing-statements"
        columns={columns}
        rows={sortedRows}
        rowKey={(s) => s.id}
        sort={sort}
        onSortChange={setSort}
        loading={loading}
        emptyText="No statements yet. Use “Print Statement” to create one."
      />

      {showPrint && (
        <GenerateModal
          loanNumber={loanNumber}
          onClose={() => setShowPrint(false)}
          onDone={() => {
            setShowPrint(false)
            load()
          }}
        />
      )}
    </div>
  )
}
